<script setup lang="ts">
import {computed, ref, watch, watchEffect} from "vue";
import {useModalStore} from "../../store/modal";
import {useUserStore} from "../../store/users";
import {useAuthStore} from "../../store/auth.js";

const props = defineProps({
    user: {
        type: Object,
        required: true
    }
})
const authStore = useAuthStore();
const userStore = useUserStore();
const modalStore = useModalStore();
const role = userStore.roles.find(role => role.value === props.user.roles[0].name)?.label ?? null
const online = ref(props.user.settings?.online ?? false)
const options = {negative: 'OFF', positive: 'ON'}
const initials = computed(() => {
    return props.user.first_name.charAt(0) + props.user.last_name.charAt(0)
})

watch(online, (value) => {
    userStore.toggleOnlineStatus(props.user.id, value)
})
const showActions = (props) => {
    modalStore.open("User", props, `#user-row-${props.id}`, 'center-center')
}
</script>

<template>
    <div class="user-row-item" :id="`user-row-${user.id}`">
        <div class="user-profile-image">
            <span>{{ initials }}</span>
        </div>
        <p class="user-profile-name">{{ `${user.first_name} ${user.last_name}` }}</p>
        <p>{{ user.email }}</p>
        <p>{{ user.contact_number }}</p>
        <p>{{ role }}</p>

        <div class="misc">
            <brand-toggle v-model="online" :options v-if="authStore.isAdmin()"/>
            <button class="user-actions" @click="showActions(user)">
                <ah-icon name="more-default"/>
            </button>
        </div>

    </div>
</template>

<style scoped lang="scss">
.user-row-item {
    display: grid;
    padding: 0 2rem;
    grid-template-columns: 4.8rem repeat(4, 1fr) 12.6rem;
    align-content: center;
    gap: 2rem;
    background: #fff;
    border-radius: 2.4rem;
    height: 8rem;
    color: var(--color-grey-thunder);
    position: relative;

    .user-profile-email,
    .user-profile-phone {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
    }


    > * {
        align-self: center;
    }

    .user-profile-image {
        height: 4.8rem;
        background: var(--color-tiffany);
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;

        span {
            @include font(1.6, 1.6, black);
            color: var(--color-white);
        }
    }

    p {
        @include font(1.4, 1.4, book);

        &.user-profile-name {
            @include font(1.4, 1.4, black);
        }
    }

    .user-actions {
        background: var(--color-white);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3.6rem;
        height: 3.6rem;
        border-radius: 50%;
        border: none;
        outline: none;

        .icon {
            fill: var(--color-grey-hazy);
        }
    }

    .misc {
        display: flex;
        gap: 2rem;
        justify-content: flex-end;
    }

    @include dark() {
        background: var(--color-darth-maul);

        .user-actions {
            background: var(--color-darth-maul);
        }
    }

    &:hover {
        background: var(--color-tiffany-dark) !important;

        p {
            color: var(--color-white);
        }
    }

    @include mobile() {
        height: auto;
        padding: 1.6rem;
        gap: 0.8rem 1.2rem;
        border-radius: 2.4rem;

        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto auto auto;
        grid-template-areas:
      "name   avatar"
      "email  avatar"
      "phone  menu"
      "role   menu";

        .user-profile-name {
            grid-area: name;
            @include font(1.6, 1.6, black);
        }
        .user-profile-email {
            grid-area: email;
        }
        .user-profile-phone {
            grid-area: phone;
        }
        .user-profile-role {
            grid-area: role;
            color: var(--color-grey-hazy);
        }

        .user-profile-image {
            grid-area: avatar;
            width: 4.8rem;
            height: 4.8rem;
            justify-self: end;
        }

        .user-actions {
            grid-area: menu;
            justify-self: end;
            align-self: start;
            width: 4.4rem;
            height: 4.4rem;
        }

        p {
            @include font(1.4, 1.6, book);
        }
    }

    @media (hover: none) {
        &:active {
            background: var(--color-tiffany-dark);

            p {
                color: var(--color-white);
            }
        }
    }
}
</style>
